import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import CommonTable from '../../../common_components/CommonTable';
import CommonTabs from '../../../common_components/CommonTabs';
import Pagination from '../../../common_components/Pagination';
import HoverTooltip from '../../../common_components/hoverOverlay/HoverTooltip';
import { EXPAND_TRIGGERS } from '../../../common_components/tableConstants';
import { f$, fK, fX, fP, fV, resultColor, resultBg, calcVariancePct, } from '../utils/formatters';
const FILTERS = ['All', 'Pass', 'Optimize', 'Fail', 'Learning', 'No Benchmark'];
const PAGE_SIZE = 100;
const GroupHeader = ({ label }) => (_jsx(Box, { sx: {
        backgroundColor: '#F4F4F5',
        color: '#252B37',
        padding: '8px',
        margin: '-10px -12px',
        fontWeight: 700,
        fontSize: '12px !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        height: '46px',
        width: 'calc(100% + 24px)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #EAECF0',
    }, children: label }));
// ── Display helpers — fix for Learning rows showing "—" (Issue #3) ───
function displayROAS(ad) {
    if (ad.atROAS != null)
        return ad.atROAS;
    if (ad.result === 'Learning' && (ad.atSpend || 0) > 0) {
        return (ad.atRevenue || 0) / ad.atSpend;
    }
    return null;
}
function displayCTROAS(ad) {
    if (ad.atCTROAS != null)
        return ad.atCTROAS;
    if (ad.result === 'Learning' && (ad.atSpend || 0) > 0) {
        return (ad.atCtRevenue || 0) / ad.atSpend;
    }
    return null;
}
function displayVTROAS(ad) {
    if (ad.atVTROAS != null)
        return ad.atVTROAS;
    if (ad.result === 'Learning' && (ad.atSpend || 0) > 0) {
        return (ad.atVtRevenue || 0) / ad.atSpend;
    }
    return null;
}
function fDateShort(s) {
    if (!s)
        return '—';
    const d = new Date(s);
    if (isNaN(d.getTime()))
        return '—';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
const AdClassificationsTab = ({ report }) => {
    const ads = useMemo(() => {
        return (report.ads || []).map((ad, idx) => ({
            ...ad,
            id: String(ad.id || ad.ad_id || ad._id || `ad-${idx}`),
        }));
    }, [report.ads]);
    const thresholdVal = report.threshold || 190;
    const [filter, setFilter] = useState('All');
    const [sortConfig, setSortConfig] = useState({
        // Default sort — afterSpend desc, per user request
        columnId: 'afterSpend',
        direction: 'desc',
    });
    const [expandedSet, setExpandedSet] = useState(new Set());
    const [page, setPage] = useState(1);
    const COLUMNS_MEMO = useMemo(() => [
        {
            id: 'at_threshold_group',
            label: `📊 AT-THRESHOLD (first $${thresholdVal})`,
            align: 'left',
            renderHeader: () => (_jsx(GroupHeader, { label: `📊 AT-THRESHOLD (first $${thresholdVal})` })),
            children: [
                { id: 'result', label: 'Result', width: '110px', sortable: true, align: 'left' },
                {
                    id: 'ad_name',
                    label: 'Ad Name',
                    width: '300px',
                    sortable: true,
                    align: 'left',
                    renderCell: (ad) => (_jsxs(Box, { sx: {
                            py: 0.5,
                            maxWidth: '280px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }, children: [_jsx(HoverTooltip, { title: ad.ad_name, placement: "top-start", children: _jsx(Typography, { noWrap: true, sx: {
                                        fontWeight: 600,
                                        fontSize: '14px !important',
                                        lineHeight: 1.25,
                                        color: '#101828',
                                        display: 'block',
                                        cursor: 'default',
                                        mb: 0.25,
                                    }, children: ad.ad_name }) }), _jsx(Typography, { sx: {
                                    fontSize: '10.5px',
                                    color: '#98A2B3',
                                    lineHeight: 1.2,
                                    fontFamily: 'monospace',
                                    display: 'block',
                                }, children: ad.ad_id })] })),
                },
                { id: 'minDate', label: 'Start', width: '110px', sortable: true, align: 'left' },
                { id: 'thresholdDate', label: `$${thresholdVal} Date`, width: '110px', sortable: true, align: 'left' },
                { id: 'atSpend', label: 'Spend', width: '110px', sortable: true, align: 'left' },
                { id: 'atOCPC', label: 'OCPC', width: '110px', sortable: true, align: 'left' },
                { id: 'atOCTR', label: 'OCTR', width: '110px', sortable: true, align: 'left' },
                { id: 'atROAS', label: 'ROAS', width: '110px', sortable: true, align: 'left' },
                { id: 'atCTROAS', label: 'CT ROAS', width: '110px', sortable: true, align: 'left' },
                { id: 'atVTROAS', label: 'VT ROAS', width: '110px', sortable: true, align: 'left' },
                { id: 'atCPA', label: 'CPA', width: '110px', sortable: true, align: 'left' },
            ],
        },
        {
            id: 'after_result_group',
            label: '🔥 AFTER-RESULT (lifetime)',
            align: 'left',
            renderHeader: () => _jsx(GroupHeader, { label: "\uD83D\uDD25 AFTER-RESULT  \u00B7  LIFETIME" }),
            children: [
                { id: 'afterSpend', label: 'After Spend', width: '120px', sortable: true, align: 'left' },
                { id: 'aftOCPC', label: 'OCPC', width: '120px', sortable: true, align: 'left' },
                { id: 'aftOCTR', label: 'OCTR', width: '120px', sortable: true, align: 'left' },
                { id: 'aftROAS', label: 'ROAS', width: '120px', sortable: true, align: 'left' },
                { id: 'aftCTROAS', label: 'CT ROAS', width: '120px', sortable: false, align: 'left' },
                { id: 'aftVTROAS', label: 'VT ROAS', width: '120px', sortable: false, align: 'left' },
                { id: 'aftCPA', label: 'CPA', width: '120px', sortable: true, align: 'left' },
                { id: 'wastedSpend', label: filter === 'Pass' ? 'Spend' : 'Wasted', width: '120px', sortable: true, align: 'left' },
                { id: 'totalRevenue', label: 'Revenue', width: '120px', sortable: true, align: 'left' },
            ],
        },
    ], [thresholdVal, filter]);
    const counts = useMemo(() => {
        const c = {
            All: ads.length,
            Pass: 0,
            Optimize: 0,
            Fail: 0,
            Learning: 0,
            'No Benchmark': 0,
        };
        for (const a of ads) {
            const r = a.result;
            if (r in c)
                c[r]++;
        }
        return c;
    }, [ads]);
    // Filter + sort the FULL list (memoised so changing page is instant)
    const sortedAll = useMemo(() => {
        let list = filter === 'All' ? ads : ads.filter((a) => a.result === filter);
        if (sortConfig) {
            const dir = sortConfig.direction === 'asc' ? 1 : -1;
            const col = sortConfig.columnId;
            const getVal = (ad) => {
                // Use display helpers so Learning ads sort correctly using
                // their on-the-fly ROAS values (Issue #3 — sort path)
                if (col === 'atROAS')
                    return displayROAS(ad) ?? -Infinity;
                if (col === 'atCTROAS')
                    return displayCTROAS(ad) ?? -Infinity;
                if (col === 'atVTROAS')
                    return displayVTROAS(ad) ?? -Infinity;
                return ad[col] ?? -Infinity;
            };
            list = [...list].sort((a, b) => {
                const av = getVal(a);
                const bv = getVal(b);
                if (typeof av === 'string')
                    return av.localeCompare(bv) * dir;
                return (av - bv) * dir;
            });
        }
        return list;
    }, [ads, filter, sortConfig]);
    // Reset to page 1 on filter or sort change
    useEffect(() => {
        setPage(1);
        setExpandedSet(new Set());
    }, [filter, sortConfig]);
    const totalEntries = sortedAll.length;
    const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    // The slice we actually feed CommonTable
    const pageRows = useMemo(() => {
        const start = (safePage - 1) * PAGE_SIZE;
        return sortedAll.slice(start, start + PAGE_SIZE);
    }, [sortedAll, safePage]);
    const handleSortChange = useCallback((columnId, direction) => {
        if (!direction)
            setSortConfig(null);
        else
            setSortConfig({ columnId, direction });
    }, []);
    const handleExpandToggle = useCallback((rowId) => {
        const id = String(rowId);
        setExpandedSet((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, []);
    const handleFilterChange = (f) => {
        setFilter(f);
    };
    const renderCell = useCallback((row, col) => {
        switch (col.id) {
            case 'result':
                return (_jsx(Chip, { label: row.result, size: "small", sx: {
                        bgcolor: resultBg(row.result),
                        color: resultColor(row.result),
                        fontWeight: 700,
                        fontSize: 10.5,
                        height: 22,
                    } }));
            case 'ad_name':
                // (handled by renderCell on the column itself)
                return null;
            case 'minDate':
                return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#475467', fontWeight: 500 }, children: fDateShort(row.minDate) }));
            case 'thresholdDate':
                return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#475467', fontWeight: 500 }, children: row.thresholdDate ? fDateShort(row.thresholdDate) : '—' }));
            case 'atSpend':
                return _jsx(Plain, { v: fK(row.atSpend), bold: true });
            case 'atOCPC':
                return ratingCell(row.atOCPC, 'currency', row.ocpcRating, row.bench?.ocpc);
            case 'atOCTR':
                return ratingCell(row.atOCTR, 'percent', row.octrRating, row.bench?.octr);
            // FIX Issue #3 — Learning rows show on-the-fly ROAS values
            case 'atROAS': {
                const val = displayROAS(row);
                if (row.result === 'Learning') {
                    return (_jsxs(Box, { sx: { whiteSpace: 'nowrap' }, children: [_jsx(Typography, { component: "div", sx: { fontSize: '14px !important', fontWeight: 700, color: '#344054' }, children: fX(val) }), row.bench?.roas != null && (_jsxs(Typography, { sx: { fontSize: 10, color: '#98A2B3', mt: 0.25 }, children: ["b:", fX(row.bench.roas)] }))] }));
                }
                return ratingCell(val, 'multiplier', row.roasRating, row.bench?.roas);
            }
            case 'atCTROAS':
                return _jsx(Plain, { v: fX(displayCTROAS(row)) });
            case 'atVTROAS':
                return _jsx(Plain, { v: fX(displayVTROAS(row)) });
            case 'atCPA':
                return _jsx(Plain, { v: f$(row.atCPA) });
            case 'afterSpend':
                if (row.result === 'Learning')
                    return _jsx(Plain, { v: "\u2014", bold: true });
                return _jsx(Plain, { v: fK(row.afterSpend), bold: true });
            case 'aftOCPC':
                return _jsx(Plain, { v: f$(row.aftOCPC) });
            case 'aftOCTR':
                return _jsx(Plain, { v: fP(row.aftOCTR) });
            case 'aftROAS':
                return _jsx(Plain, { v: fX(row.aftROAS), bold: true });
            case 'aftCTROAS':
                return _jsx(Plain, { v: fX(row.aftCTROAS) });
            case 'aftVTROAS':
                return _jsx(Plain, { v: fX(row.aftVTROAS) });
            case 'aftCPA':
                return _jsx(Plain, { v: f$(row.aftCPA) });
            case 'wastedSpend':
                if (row.result === 'Learning')
                    return _jsx(Plain, { v: "\u2014", bold: true });
                if (filter === 'Pass') {
                    return _jsx(Plain, { v: fK(row.wastedSpend), bold: true });
                }
                return (_jsx(Typography, { sx: {
                        fontSize: '14px !important',
                        fontWeight: 700,
                        color: (row.wastedSpend || 0) > 0
                            ? resultColor('Fail')
                            : '#475467',
                    }, children: fK(row.wastedSpend) }));
            case 'totalRevenue':
                return _jsx(Plain, { v: fK(row.totalRevenue), bold: true });
            default:
                return null;
        }
    }, [filter]);
    // FIX Issue #2 — Expanded row matches standalone exactly (12 cards in
    // flex-wrap layout). Per index.html lines 1252-1307.
    const renderExpandedContent = useCallback((row) => {
        const totalS = (row.atSpend || 0) + (row.afterSpend || 0);
        const totalR = (row.atRevenue || 0) + (row.afterRevenue || 0);
        const totalROAS = totalS > 0 ? totalR / totalS : null;
        // For Learning, use the same on-the-fly compute
        const ctRoas = displayCTROAS(row);
        const vtRoas = displayVTROAS(row);
        // Benchmark Window display — Batch 9 anchor/window system
        const benchWindowLabel = (row.benchWindowStart && row.benchWindowEnd)
            ? `${row.benchWindowStart} → ${row.benchWindowEnd}`
            : (row.benchKey || 'None');
        const benchDurationLabel = row.benchWindowDays
            ? `${row.benchWindowDays}d`
            : (row.benchKey && !row.benchKey.includes('→')
                ? 'legacy'
                : '—');
        return (_jsxs(Box, { sx: {
                px: 1.75,
                py: 1,
                bgcolor: '#F9FAFB',
                display: 'grid',
                gridTemplateColumns: 'fit-content(240px) 1.4fr 1.2fr',
                gap: 1.25,
                alignItems: 'stretch',
            }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsxs(Box, { children: [_jsx(SectionLabel, { children: "Benchmark" }), _jsxs(Box, { sx: {
                                        bgcolor: '#fff',
                                        border: '1px solid #EAECF0',
                                        borderRadius: '6px',
                                        px: 1,
                                        py: 0.75,
                                        width: 'fit-content',
                                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.35, flexWrap: 'wrap' }, children: [_jsx(Typography, { sx: { fontSize: 11.5, fontWeight: 700, color: '#101828' }, children: benchWindowLabel }), _jsx(Chip, { label: benchDurationLabel, size: "small", sx: {
                                                        bgcolor: '#FCE8F2',
                                                        color: '#EE2A7B',
                                                        fontWeight: 700,
                                                        fontSize: 9.5,
                                                        height: 16,
                                                        '& .MuiChip-label': { px: 0.6 },
                                                    } }), row.benchIsCustom && (_jsx(Typography, { sx: { fontSize: 9, color: '#92400E', fontWeight: 600 }, children: "custom" }))] }), _jsxs(Box, { sx: { display: 'flex', gap: 1.25, fontSize: 10.5, color: '#475467' }, children: [_jsxs("span", { children: ["OCPC ", _jsx("strong", { style: { color: '#101828' }, children: f$(row.bench?.ocpc) })] }), _jsxs("span", { children: ["OCTR ", _jsx("strong", { style: { color: '#101828' }, children: fP(row.bench?.octr) })] }), _jsxs("span", { children: ["ROAS ", _jsx("strong", { style: { color: '#101828' }, children: fX(row.bench?.roas) })] })] })] })] }), _jsxs(Box, { sx: { mt: 0.85 }, children: [_jsx(SectionLabel, { children: "Ad ID" }), _jsx(Box, { sx: {
                                        bgcolor: '#fff',
                                        border: '1px solid #EAECF0',
                                        borderRadius: '6px',
                                        px: 1,
                                        py: 0.75,
                                        width: 'fit-content',
                                    }, children: _jsx(Typography, { sx: {
                                            fontFamily: 'monospace',
                                            fontSize: 10.5,
                                            color: '#475467',
                                            wordBreak: 'break-all',
                                        }, children: row.ad_id }) })] })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx(SectionLabel, { children: "At-Threshold Metrics" }), _jsxs(Box, { sx: {
                                bgcolor: '#fff',
                                border: '1px solid #EAECF0',
                                borderRadius: '6px',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                                flex: 1,
                                '& > *': {
                                    px: 0.85,
                                    py: 0.5,
                                    borderRight: '1px solid #F2F4F7',
                                    borderBottom: '1px solid #F2F4F7',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                },
                                '& > *:nth-of-type(4n)': {
                                    borderRight: 'none',
                                },
                                '& > *:nth-last-of-type(-n+4)': {
                                    borderBottom: 'none',
                                },
                            }, children: [_jsx(MetricCell, { label: "Purchases", value: String(row.atPurchases || 0) }), _jsx(MetricCell, { label: "CT ROAS", value: fX(ctRoas) }), _jsx(MetricCell, { label: "VT ROAS", value: fX(vtRoas) }), _jsx(MetricCell, { label: "CPA", value: f$(row.atCPA) }), _jsx(MetricCell, { label: "Clicks", value: (row.atClicks || 0).toLocaleString('en-US') }), _jsx(MetricCell, { label: "Impr.", value: (row.atImpressions || 0).toLocaleString('en-US') }), _jsx(MetricCell, { label: "Spend", value: fK(row.atSpend) }), _jsx(MetricCell, { label: "Revenue", value: fK(row.atRevenue) })] })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx(SectionLabel, { children: "Report Period" }), _jsxs(Box, { sx: {
                                bgcolor: '#fff',
                                border: '1px solid #EAECF0',
                                borderRadius: '6px',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                flex: 1,
                                '& > *': {
                                    px: 0.85,
                                    py: 0.5,
                                    borderRight: '1px solid #F2F4F7',
                                    borderBottom: '1px solid #F2F4F7',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                },
                                '& > *:last-child': {
                                    borderRight: 'none',
                                },
                                '& > *:nth-last-of-type(-n+3)': {
                                    borderBottom: 'none',
                                },
                            }, children: [_jsx(PeriodCol, { label: "Revenue", value: fK(totalR), sub: `${fK(row.atRevenue || 0)} + ${fK(row.afterRevenue || 0)}` }), _jsx(PeriodCol, { label: "Spend", value: fK(totalS), sub: `${fK(row.atSpend || 0)} + ${fK(row.afterSpend || 0)}` }), _jsx(PeriodCol, { label: "ROAS", value: totalROAS != null ? fX(totalROAS) : '—', sub: "rev / spend", color: totalROAS != null && totalROAS >= 1
                                        ? '#027A48'
                                        : totalROAS != null
                                            ? '#B42318'
                                            : undefined })] })] })] }));
    }, []);
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: {
                    px: 2,
                    py: 1,
                    bgcolor: '#FFFBEB',
                    borderBottom: '1px solid #FEF3C7',
                    fontSize: 11,
                    color: '#92400E',
                    lineHeight: 1.5,
                }, children: ["\uD83D\uDCA1 After-result columns show ", _jsx("strong", { children: "lifetime" }), " values \u00B7 click a row for ", _jsx("strong", { children: "report-period" }), " totals."] }), _jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    borderBottom: '1px solid #EAECF0',
                    bgcolor: '#fff',
                }, children: _jsx(CommonTabs, { selectedValue: filter, onChange: (v) => handleFilterChange(v), tabs: FILTERS.map((f) => ({
                        value: f,
                        label: `${f} ${counts[f] ?? 0}`,
                    })) }) }), _jsx(Box, { sx: {
                    '& .reusable-table-header-cell': {
                        height: '42px !important',
                        padding: '8px 12px !important',
                        fontSize: '11px',
                        fontWeight: 700,
                        bgcolor: '#F9FAFB !important',
                    },
                    '& .reusable-table-header-cell, & .reusable-table-cell': {
                        whiteSpace: 'nowrap',
                    },
                    '& .reusable-table-cell': {
                        height: '54px !important',
                        padding: '6px 12px !important',
                    },
                    /* Group Separator for AFTER-RESULT */
                    '& [data-column-id="after_result_group"], & [data-column-id="afterSpend"]': {
                        borderLeft: '1px solid #D0D5DD !important',
                    },
                }, children: _jsx(CommonTable, { columns: COLUMNS_MEMO, rows: pageRows, sortConfig: sortConfig, onSortChange: handleSortChange, expandTrigger: EXPAND_TRIGGERS.ROW, onExpandToggle: handleExpandToggle, controlledExpandedRows: expandedSet, renderExpandedContent: renderExpandedContent, renderCell: renderCell, stickyHeader: true, maxHeight: "calc(100vh - 200px)", pinnedColumns: { left: ['result', 'ad_name'], right: [] }, emptyMessage: "No ads in this filter.", rowSx: () => ({
                        '&:hover': { bgcolor: '#F9FAFB' },
                    }) }) }), totalEntries > 0 && (_jsx(Box, { sx: {
                    p: 2,
                    borderTop: '1px solid #EAECF0',
                    bgcolor: '#fff',
                }, children: _jsx(Pagination, { currentPage: safePage, totalPages: totalPages, onPageChange: setPage, maxVisiblePages: 5, totalEntries: totalEntries, itemsPerPage: PAGE_SIZE, showEntriesMessage: true }) }))] }));
};
// ── Cell helpers — fonts/colors tuned to match the OLD standalone report ──
const Plain = ({ v, bold }) => (_jsx(Typography, { sx: {
        fontSize: '14px !important',
        fontWeight: bold ? 700 : 500,
        color: '#475467',
    }, children: v }));
// ── Helpers used by the expanded-row layout ─────────────────────────────────
const SectionLabel = ({ children }) => (_jsx(Typography, { sx: {
        fontSize: 8.5,
        fontWeight: 700,
        color: '#98A2B3',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        mb: 0.35,
    }, children: children }));
const MetricCell = ({ label, value }) => (_jsxs(Box, { children: [_jsx(Typography, { sx: {
                fontSize: 8.5,
                color: '#98A2B3',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                mb: 0.15,
                lineHeight: 1.1,
            }, children: label }), _jsx(Typography, { sx: { fontSize: 11.5, fontWeight: 700, color: '#101828', lineHeight: 1.15 }, children: value })] }));
const PeriodCol = ({ label, value, sub, color }) => (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx(Typography, { sx: {
                fontSize: 8.5,
                color: '#98A2B3',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                mb: 0.15,
                lineHeight: 1.1,
            }, children: label }), _jsx(Typography, { sx: {
                fontSize: 11.5,
                fontWeight: 700,
                color: color || '#101828',
                lineHeight: 1.15,
            }, children: value }), sub && (_jsx(Typography, { sx: { fontSize: 9, color: '#98A2B3', mt: 0.25, lineHeight: 1.1, whiteSpace: 'nowrap' }, children: sub }))] }));
function ratingCell(val, fmt, rating, bench) {
    const display = fmt === 'currency' ? f$(val) : fmt === 'multiplier' ? fX(val) : fP(val);
    const variance = calcVariancePct(val ?? null, bench ?? null);
    const color = rating ? resultColor(rating) : '#344054';
    return (_jsxs(Box, { sx: { whiteSpace: 'nowrap' }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: '14px !important', color, lineHeight: 1.2 }, component: "div", children: display }), bench != null && val != null && (_jsxs(Typography, { sx: {
                    fontSize: 10,
                    color: '#98A2B3',
                    mt: 0.25,
                    fontFamily: 'monospace',
                }, children: ["b:", fmt === 'currency'
                        ? f$(bench)
                        : fmt === 'multiplier'
                            ? fX(bench)
                            : fP(bench)] })), rating && variance != null && (_jsx(Typography, { sx: { fontSize: 10, fontWeight: 700, color }, children: fV(variance) }))] }));
}
export default AdClassificationsTab;
